import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import '../asset/css/Dashboard.css';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Authentication check effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (!u || u.email !== '123@gmail.com') {
        navigate('/', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  
  // Students state with initial state loaded from localStorage
  const [students, setStudents] = useState(() => {
    const savedStudents = localStorage.getItem('local_students_data');
    return savedStudents ? JSON.parse(savedStudents) : [];
  });

  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState('');
  
  // Users state from Firestore users collection
  const [usersList, setUsersList] = useState(() => {
    const savedUsers = localStorage.getItem('local_users_data');
    return savedUsers ? JSON.parse(savedUsers) : [];
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Loading states for add and save buttons
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Cert Add/Save loading states
  const [isCertAdding, setIsCertAdding] = useState(false);
  const [isCertSaving, setIsCertSaving] = useState(false);

  // Forms state with initial state loaded from localStorage
  const [formsData, setFormsData] = useState(() => {
    const savedForms = localStorage.getItem('local_forms_data');
    return savedForms ? JSON.parse(savedForms) : [];
  });
  
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);

  // Certificates state with initial state loaded from localStorage
  const [certificates, setCertificates] = useState(() => {
    const savedCerts = localStorage.getItem('local_certificates_data');
    return savedCerts ? JSON.parse(savedCerts) : [];
  });

  const [certsLoading, setCertsLoading] = useState(false);
  const [certsError, setCertsError] = useState('');

  // Certificate Modal States
  const [isAddCertModalOpen, setIsAddCertModalOpen] = useState(false);
  const [isEditCertModalOpen, setIsEditCertModalOpen] = useState(false);

  const [addCertForm, setAddCertForm] = useState({
    displayName: '',
    email: '',
    course: 'Web Development-1 online',
    qualifyDate: '',
    grade: 'A',
    examScore: '',
    assignmentScore: '',
    certificateId: '',
  });

  const [editingCert, setEditingCert] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [certSearchTerm, setCertSearchTerm] = useState('');

  // Fetch data on load
  useEffect(() => {
    fetchUsers();
    fetchStudents();
    fetchForms();
    fetchCertificates();
  }, []);

  const fetchStudents = async () => {
    setStudentsLoading(true);
    setStudentsError('');
    try {
      const response = await fetch('https://mmao.vercel.app/get/all/students');
      const result = await response.json();
      if (result.status === 'success') {
        setStudents(result.data);
        localStorage.setItem('local_students_data', JSON.stringify(result.data));
      } else {
        setStudentsError(result.text || 'Failed to fetch students.');
      }
    } catch (err) {
      console.error(err);
      setStudentsError('Network error while connecting to server.');
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await fetch('https://mmao.vercel.app/get/all/users');
      const result = await response.json();
      if (result.status === 'success') {
        setUsersList(result.data);
        localStorage.setItem('local_users_data', JSON.stringify(result.data));
      } else {
        setUsersError(result.text || 'Failed to fetch users.');
      }
    } catch (err) {
      console.error(err);
      setUsersError('Network error while connecting to server.');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchForms = async () => {
    setFormsLoading(true);
    setFormsError('');
    try {
      const response = await fetch('https://mmao.vercel.app/get/all/forms');
      const result = await response.json();
      if (result.status === 'success') {
        setFormsData(result.data);
        localStorage.setItem('local_forms_data', JSON.stringify(result.data));
      } else {
        setFormsError(result.text || 'Failed to fetch forms.');
      }
    } catch (err) {
      console.error(err);
      setFormsError('Network error while connecting to server.');
    } finally {
      setFormsLoading(false);
    }
  };

  const fetchCertificates = async () => {
    setCertsLoading(true);
    setCertsError('');
    try {
      const response = await fetch('https://mmao.vercel.app/get/all/certificates');
      const result = await response.json();
      if (result.status === 'success') {
        setCertificates(result.data);
        localStorage.setItem('local_certificates_data', JSON.stringify(result.data));
      } else {
        setCertsError(result.text || 'Failed to fetch certificates.');
      }
    } catch (err) {
      console.error(err);
      setCertsError('Network error while connecting to server.');
    } finally {
      setCertsLoading(false);
    }
  };

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Add Form State with address and gender added
  const [addForm, setAddForm] = useState({
    email: '',
    studentClass: 'web-dev-1-online',
    status: 'enrolled',
    address: '',
    gender: 'Male',
  });

  const [editingStudent, setEditingStudent] = useState(null);

  const handleOpenAddModal = () => {
    setAddForm({ email: '', studentClass: 'web-dev-1-online', status: 'enrolled', address: '', gender: 'Male' });
    setIsAddModalOpen(true);
  };

  const handleSaveNewStudent = async (e) => {
    e.preventDefault();
    if (!addForm.email) return;

    setIsAdding(true);
    try {
      const response = await fetch('https://mmao.vercel.app/add/course/student', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      const result = await response.json();

      if (result.status === 'success') {
        const updatedStudents = [...students, result.data];
        setStudents(updatedStudents);
        localStorage.setItem('local_students_data', JSON.stringify(updatedStudents));
        setIsAddModalOpen(false);
      } else {
        alert(result.text || 'Failed to add student.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while connecting to server.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenEditModal = (student) => {
    setEditingStudent({ ...student });
    setIsEditModalOpen(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editingStudent) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://mmao.vercel.app/edit/course/student', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingStudent),
      });
      const result = await response.json();

      if (result.status === 'success') {
        const updatedStudents = students.map(s => s.id === editingStudent.id ? editingStudent : s);
        setStudents(updatedStudents);
        localStorage.setItem('local_students_data', JSON.stringify(updatedStudents));
        setIsEditModalOpen(false);
        setEditingStudent(null);
      } else {
        alert(result.text || 'Failed to update student.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while connecting to server.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStudent = (id) => {
    if (window.confirm('Are you sure you want to delete this student record?')) {
      const updatedStudents = students.filter(s => s.id !== id);
      setStudents(updatedStudents);
      localStorage.setItem('local_students_data', JSON.stringify(updatedStudents));
      setIsEditModalOpen(false);
    }
  };

  const handleOpenAddCertModal = () => {
    const today = new Date().toISOString().split('T')[0];
    setAddCertForm({
      displayName: '',
      email: '',
      course: 'Web Development-1 online',
      qualifyDate: today,
      grade: 'A',
      examScore: '',
      assignmentScore: '',
      addDate: today,
      certificateId: 'CERT-' + Math.floor(100000 + Math.random() * 900000),
    });
    setIsAddCertModalOpen(true);
  };

  const handleSaveNewCertificate = async (e) => {
    e.preventDefault();
    if (!addCertForm.email) return;

    setIsCertAdding(true);
    try {
      const response = await fetch('https://mmao.vercel.app/add/certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addCertForm),
      });
      const result = await response.json();

      if (result.status === 'success') {
        const updatedCerts = [...certificates, result.data];
        setCertificates(updatedCerts);
        localStorage.setItem('local_certificates_data', JSON.stringify(updatedCerts));
        setIsAddCertModalOpen(false);
      } else {
        alert(result.text || 'Failed to add certificate.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while connecting to server.');
    } finally {
      setIsCertAdding(false);
    }
  };

  const handleOpenEditCertModal = (cert) => {
    setEditingCert({ ...cert });
    setIsEditCertModalOpen(true);
  };

  const handleUpdateCertificate = async (e) => {
    e.preventDefault();
    if (!editingCert) return;

    setIsCertSaving(true);
    try {
      const response = await fetch('https://mmao.vercel.app/edit/certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingCert),
      });
      const result = await response.json();

      if (result.status === 'success') {
        const updatedCerts = certificates.map(c => c.id === editingCert.id ? editingCert : c);
        setCertificates(updatedCerts);
        localStorage.setItem('local_certificates_data', JSON.stringify(updatedCerts));
        setIsEditCertModalOpen(false);
        setEditingCert(null);
      } else {
        alert(result.text || 'Failed to update certificate.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while connecting to server.');
    } finally {
      setIsCertSaving(false);
    }
  };

  const filteredStudents = students.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.studentClass.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCertificates = certificates.filter(c => 
    (c.displayName && c.displayName.toLowerCase().includes(certSearchTerm.toLowerCase())) ||
    (c.email && c.email.toLowerCase().includes(certSearchTerm.toLowerCase())) ||
    (c.course && c.course.toLowerCase().includes(certSearchTerm.toLowerCase())) ||
    (c.certificateId && c.certificateId.toLowerCase().includes(certSearchTerm.toLowerCase()))
  );

  const filteredUsers = usersList.filter(u =>
    (u.displayName && u.displayName.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
    (u.uid && u.uid.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  // Student Class Breakdown for Pie Chart
  const classCounts = students.reduce((acc, curr) => {
    const cls = curr.studentClass || 'Unassigned';
    acc[cls] = (acc[cls] || 0) + 1;
    return acc;
  }, {});

  const totalStudentsCount = students.length || 1;
  const classColors = ['#4f46e5', '#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  let accumulatedPercentage = 0;
  const pieSlices = Object.entries(classCounts).map(([className, count], index) => {
    const percentage = (count / totalStudentsCount) * 100;
    const startAngle = accumulatedPercentage;
    accumulatedPercentage += percentage;
    return {
      className,
      count,
      percentage: percentage.toFixed(1),
      color: classColors[index % classColors.length],
      startAngle,
      endAngle: accumulatedPercentage
    };
  });

  const conicGradientString = pieSlices.length > 0 
    ? pieSlices.map(slice => `${slice.color} ${slice.startAngle}% ${slice.endAngle}%`).join(', ')
    : '#e2e8f0 0% 100%';

  // Certificate Grade Breakdown Calculations
  const gradeCounts = certificates.reduce((acc, curr) => {
    const grade = curr.grade || 'N/A';
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  const totalCertsCount = certificates.length || 1;
  const gradeColors = { 'A+': '#9333ea', 'A': '#4f46e5', 'B': '#0284c7', 'C': '#f59e0b', 'N/A': '#64748b' };

  let accCertPercentage = 0;
  const certPieSlices = Object.entries(gradeCounts).map(([grade, count]) => {
    const percentage = (count / totalCertsCount) * 100;
    const startAngle = accCertPercentage;
    accCertPercentage += percentage;
    return {
      grade,
      count,
      percentage: percentage.toFixed(1),
      color: gradeColors[grade] || '#3730a3',
      startAngle,
      endAngle: accCertPercentage
    };
  });

  const certConicGradientString = certPieSlices.length > 0 
    ? certPieSlices.map(slice => `${slice.color} ${slice.startAngle}% ${slice.endAngle}%`).join(', ')
    : '#e2e8f0 0% 100%';

  return (
    <div className="dashboard-container">
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      {/* Left Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="dashboard-sidebar-logo">
          <svg className="dashboard-logo-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
          <span className="dashboard-logo-text">MMao Portal</span>
          <button className="close-sidebar-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>
        </div>
        
        <nav className="dashboard-sidebar-nav">
          {[
            { name: 'Dashboard', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
            { name: 'Users', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            { name: 'Student', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            { name: 'Class', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-0-5H20"/></svg> },
            { name: 'Form', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
            { name: 'Certificate', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
            { name: 'Import/Export', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="8 21 3 21 3 16"/></svg> },
          ].map((item) => (
            <button
              key={item.name}
              className={`dashboard-nav-item ${activeTab === item.name ? 'active' : ''}`}
              onClick={() => { setActiveTab(item.name); setIsSidebarOpen(false); }}
            >
              <span className="dashboard-nav-icon">{item.icon}</span>
              <span className="nav-label">{item.name}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Wrapper */}
      <div className="main-wrapper">
        <header className="dashboard-top-header">
          <div className="header-left-group" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button className="mobile-menu-toggle" onClick={() => setIsSidebarOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <div className="header-title-group">
              <h1>{activeTab} Overview</h1>
              <span className="header-date">30 October 2026</span>
            </div>
          </div>
          <div className="header-right-actions">
            <div className="admin-profile-pill">
              <span className="admin-name">Aon Mao</span>
            </div>
            <button className="logout-btn" onClick={() => alert('Logged out successfully!')}>
              <span>Log Out</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </header>

        {/* Dynamic Content Area */}
        <main className="content-area">
          {activeTab === 'Dashboard' && (
            <div className="dashboard-overview-grid">
              {/* Stat Cards - 4 Cards without Revenue */}
              <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="stat-card primary-stat">
                  <div className="stat-icon-wrap">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  </div>
                  <div className="stat-info">
                    <p>Total Users</p>
                    <h3>{usersList.length}</h3>
                    <span className="trend-badge positive">Firestore Users</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap" style={{ background: '#dcfce7', color: '#16a34a' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  </div>
                  <div className="stat-info">
                    <p>Total Students</p>
                    <h3>{students.length}</h3>
                    <span className="trend-badge positive">Enrolled Records</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap blue">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  </div>
                  <div className="stat-info">
                    <p>Total Forms</p>
                    <h3>{formsData.length}</h3>
                    <span className="trend-badge positive">Submissions</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap purple">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  </div>
                  <div className="stat-info">
                    <p>Certificates</p>
                    <h3>{certificates.length}</h3>
                    <span className="trend-badge positive">Issued Records</span>
                  </div>
                </div>
              </div>

              {/* Individual Charts Grid for Each Metric */}
              <div className="middle-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                
                {/* 1. Total Users Overview */}
                <div className="card">
                  <div className="card-header">
                    <h3>Users Overview</h3>
                    <span className="badge-pill">{usersList.length} Total</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '10px 0' }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Registered accounts synchronized in Firebase Firestore database.</p>
                    <div style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                      <div style={{ flex: 1, background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <small style={{ color: 'var(--text-muted)' }}>Active Users</small>
                        <h4 style={{ fontSize: '20px', color: 'var(--color)', marginTop: '4px' }}>{usersList.length}</h4>
                      </div>
                      <div style={{ flex: 1, background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <small style={{ color: 'var(--text-muted)' }}>Roles Connected</small>
                        <h4 style={{ fontSize: '20px', color: 'var(--theme)', marginTop: '4px' }}>Students / Admin</h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Total Students PIE CHART Breakdown by Class */}
                <div className="card">
                  <div className="card-header">
                    <h3>Students by Class (Pie Chart)</h3>
                    <span className="badge-pill">{students.length} Total Students</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '15px 0', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{
                      width: '130px', height: '130px', borderRadius: '50%',
                      background: `conic-gradient(${conicGradientString})`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)', flexShrink: 0
                    }}></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1, minWidth: '160px' }}>
                      {pieSlices.length > 0 ? pieSlices.map((slice, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: slice.color }}></span>
                            <span style={{ color: 'var(--color)', fontWeight: '500' }}>{slice.className}</span>
                          </div>
                          <span style={{ color: 'var(--text-muted)', fontWeight: '600' }}>{slice.count} ({slice.percentage}%)</span>
                        </div>
                      )) : (
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No student class data available.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 3. Total Forms Submissions Chart */}
                <div className="card">
                  <div className="card-header">
                    <h3>Course Submissions Forms</h3>
                    <span className="badge-pill">{formsData.length} Submissions</span>
                  </div>
                  <div className="bar-chart" style={{ height: '160px' }}>
                    <div className="y-axis">
                      <span>100%</span><span>50%</span><span>0%</span>
                    </div>
                    <div className="bars-container">
                      <div className="bar-group"><div className="bar active-bar" style={{height: `${Math.min(formsData.length * 15, 100)}%`}}></div><span>Forms</span></div>
                      <div className="bar-group"><div className="bar" style={{height: '60%'}}></div><span>Pending</span></div>
                      <div className="bar-group"><div className="bar" style={{height: '80%'}}></div><span>Approved</span></div>
                      <div className="bar-group"><div className="bar" style={{height: '40%'}}></div><span>Archived</span></div>
                    </div>
                  </div>
                </div>

                {/* 4. Certificates Chart (Breakdown by Grade) */}
                <div className="card">
                  <div className="card-header">
                    <h3>Certificates by Grade (Chart)</h3>
                    <span className="badge-pill">{certificates.length} Issued</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '15px 0', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{
                      width: '130px', height: '130px', borderRadius: '50%',
                      background: `conic-gradient(${certConicGradientString})`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)', flexShrink: 0
                    }}></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1, minWidth: '160px' }}>
                      {certPieSlices.length > 0 ? certPieSlices.map((slice, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: slice.color }}></span>
                            <span style={{ color: 'var(--color)', fontWeight: '500' }}>Grade {slice.grade}</span>
                          </div>
                          <span style={{ color: 'var(--text-muted)', fontWeight: '600' }}>{slice.count} ({slice.percentage}%)</span>
                        </div>
                      )) : (
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No certificate grade data available.</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'Users' && (
            <div className="tab-content-container">
              <div className="tab-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <input 
                    type="text" 
                    placeholder="Search users by name, email or UID..." 
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="badge-pill" style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '600' }}>
                    Total: {usersList.length} users
                  </span>
                </div>
                <button className="add-student-btn refresh-btn-custom" onClick={fetchUsers} style={{ backgroundColor: '#0284c7' }}>
                  Get Data / Refresh
                </button>
              </div>

              {usersLoading ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>Loading users from Firestore...</p> : null}
              {usersError ? <p style={{ color: '#ef4444', textAlign: 'center', padding: '15px' }}>{usersError}</p> : null}

              {usersList.length === 0 && !usersLoading ? (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>No users found. Click "Get Data / Refresh" to fetch.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '10px' }}>
                  {filteredUsers.map((u) => (
                    <div key={u.id || u.uid} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '45px', height: '45px', borderRadius: '50%', background: 'var(--theme-gradient)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '18px', flexShrink: 0
                        }}>
                          {u.displayName ? u.displayName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                          <h4 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--color)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            {u.displayName || 'No Name'}
                          </h4>
                          <span className="badge-pill" style={{ marginTop: '4px', display: 'inline-block' }}>{u.role || 'student'}</span>
                        </div>
                      </div>

                      <div style={{ fontSize: '13px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                        <p><strong>Email:</strong> {u.email || 'N/A'}</p>
                        <p><strong>Address:</strong> {u.address || 'N/A'}</p>
                        <p style={{ fontSize: '11px', wordBreak: 'break-all' }}><strong>UID:</strong> {u.uid || u.id}</p>
                        {u.createdAt && <p style={{ fontSize: '11px' }}><strong>Joined:</strong> {u.createdAt}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STUDENT TAB */}
          {activeTab === 'Student' && (
            <div className="tab-content-container">
              <div className="tab-toolbar">
                <input 
                  type="text" 
                  placeholder="Search students by email, class or status..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <div className="toolbar-actions-group">
                  <button className="add-student-btn refresh-btn-custom" onClick={fetchStudents} style={{ backgroundColor: '#0284c7' }}>Get Data / Refresh</button>
                  <button className="add-student-btn" onClick={handleOpenAddModal}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Student
                  </button>
                </div>
              </div>

              {studentsLoading ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>Loading students from API...</p> : null}
              {studentsError ? <p style={{ color: '#ef4444', textAlign: 'center', padding: '15px' }}>{studentsError}</p> : null}

              <div className="card table-card">
                <h3>Student Directory</h3>
                <div className="table-responsive-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Email</th>
                        <th>Class</th>
                        <th>Status</th>
                        <th>Gender</th>
                        <th>Address</th>
                        <th>Join Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.length > 0 ? filteredStudents.map(s => (
                        <tr key={s.id}>
                          <td>#{s.id}</td>
                          <td><strong>{s.email}</strong></td>
                          <td>{s.studentClass}</td>
                          <td>
                            <span className={`status-badge status-${s.status}`}>
                              {s.status}
                            </span>
                          </td>
                          <td>{s.gender || 'N/A'}</td>
                          <td>{s.address || 'N/A'}</td>
                          <td>{s.joinDate}</td>
                          <td>
                            <button className="icon-action-btn edit-pen-btn" onClick={() => handleOpenEditModal(s)} title="Edit Student">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No student records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* FORM TAB */}
          {activeTab === 'Form' && (
            <div className="tab-content-container">
              <div className="tab-toolbar" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Course Submission Forms List</h3>
                <button className="add-student-btn" onClick={fetchForms}>Get Data / Refresh</button>
              </div>

              {formsLoading ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>Loading form submissions from API...</p> : null}
              {formsError ? <p style={{ color: '#ef4444', textAlign: 'center', padding: '15px' }}>{formsError}</p> : null}

              {formsData.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>No forms data found in local storage. Click "Get Data / Refresh" to fetch from API.</p>
              ) : (
                <div className="card table-card">
                  <div className="table-responsive-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Course</th>
                          <th>Time</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formsData.map((form) => (
                          <tr key={form.id}>
                            <td><strong>{form.firstName} {form.lastName}</strong></td>
                            <td>{form.emailAddress}</td>
                            <td><span className="badge-pill">{form.course || 'N/A'}</span></td>
                            <td>{form.time || 'N/A'}</td>
                            <td>
                              <button className="primary-btn" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => setSelectedForm(form)}>
                                View Detail
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CERTIFICATE TAB */}
          {activeTab === 'Certificate' && (
            <div className="tab-content-container">
              <div className="tab-toolbar">
                <input 
                  type="text" 
                  placeholder="Search certificates by name, email, course or cert ID..." 
                  value={certSearchTerm}
                  onChange={(e) => setCertSearchTerm(e.target.value)}
                  className="search-input"
                />
                <div className="toolbar-actions-group">
                  <button className="add-student-btn refresh-btn-custom" onClick={fetchCertificates} style={{ backgroundColor: '#0284c7' }}>Get Data / Refresh</button>
                  <button className="add-student-btn" onClick={handleOpenAddCertModal}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Certificate
                  </button>
                </div>
              </div>

              {certsLoading ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>Loading certificates from API...</p> : null}
              {certsError ? <p style={{ color: '#ef4444', textAlign: 'center', padding: '15px' }}>{certsError}</p> : null}

              <div className="card table-card">
                <h3>Issued Certificates List</h3>
                <div className="table-responsive-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Cert ID</th>
                        <th>Display Name</th>
                        <th>Email</th>
                        <th>Course</th>
                        <th>Grade</th>
                        <th>Issue Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCertificates.length > 0 ? filteredCertificates.map(c => (
                        <tr key={c.id}>
                          <td><Link to={'/certificate/'+c.certificateId}><strong>{c.certificateId}</strong></Link></td>
                          <td>{c.displayName}</td>
                          <td>{c.email}</td>
                          <td><span className="badge-pill">{c.course}</span></td>
                          <td>{c.grade}</td>
                          <td>{c.addDate || c.qualifyDate}</td>
                          <td>
                            <button className="icon-action-btn edit-pen-btn" onClick={() => handleOpenEditCertModal(c)} title="Edit Certificate">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No certificate records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* FORM DETAIL POPUP MODAL */}
          {selectedForm && (
            <div className="modal-backdrop" onClick={() => setSelectedForm(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                  <h3>Submission Detail</h3>
                  <button className="close-modal-btn" onClick={() => setSelectedForm(null)}>✕</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: 'var(--color)' }}>
                  <p><strong>Full Name:</strong> {selectedForm.firstName} {selectedForm.lastName}</p>
                  <p><strong>Email Address:</strong> {selectedForm.emailAddress}</p>
                  <p><strong>Course:</strong> {selectedForm.course}</p>
                  <p><strong>Address:</strong> {selectedForm.address}, {selectedForm.city}, {selectedForm.country}</p>
                  <p><strong>Role & Age:</strong> {selectedForm.role} ({selectedForm.age} years old)</p>
                  <p><strong>Gender:</strong> {selectedForm.gender}</p>
                  <p><strong>Computer Skill:</strong> {selectedForm.comskill}</p>
                  <p><strong>Interests:</strong> {selectedForm.interesting}</p>
                  <p><strong>Expectations:</strong> {selectedForm.expectations}</p>
                  <p><strong>Submission Date:</strong> {selectedForm.submission}</p>
                  <p><strong>Created Time:</strong> {selectedForm.time}</p>
                </div>
                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button type="button" className="primary-btn" onClick={() => setSelectedForm(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {/* ADD STUDENT MODAL - Updated with Address and Gender select */}
          {isAddModalOpen && (
            <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header">
                  <h3>Add New Student</h3>
                  <button className="close-modal-btn" onClick={() => setIsAddModalOpen(false)}>✕</button>
                </div>
                <form onSubmit={handleSaveNewStudent}>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="e.g. john@school.com" required />
                  </div>
                  <div className="form-group">
                    <label>Class</label>
                    <select value={addForm.studentClass} onChange={(e) => setAddForm({ ...addForm, studentClass: e.target.value })}>
                      <option value="Web Development-1 online">Web Development-1 online</option>
                      <option value="Web Development-1 offline">Web Development-1 offline</option>
                      <option value="Web Development-2 online">Web Development-2 online</option>
                      <option value="Linux noob to pro">Linux noob to pro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select value={addForm.gender} onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} placeholder="e.g. Namhkam, Shan State" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={addForm.status} onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}>
                      <option value="enrolled">Enrolled</option>
                      <option value="suspend">Suspend</option>
                      <option value="removed">Removed</option>
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-btn" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                    <button type="submit" className="primary-btn" disabled={isAdding}>{isAdding ? 'Saving...' : 'Save Student'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ADD CERTIFICATE MODAL */}
          {isAddCertModalOpen && (
            <div className="modal-backdrop" onClick={() => setIsAddCertModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header">
                  <h3>Add New Certificate</h3>
                  <button className="close-modal-btn" onClick={() => setIsAddCertModalOpen(false)}>✕</button>
                </div>
                <form onSubmit={handleSaveNewCertificate}>
                  <div className="form-group"><label>Display Name</label><input type="text" value={addCertForm.displayName} onChange={(e) => setAddCertForm({ ...addCertForm, displayName: e.target.value })} required /></div>
                  <div className="form-group"><label>Email Address</label><input type="email" value={addCertForm.email} onChange={(e) => setAddCertForm({ ...addCertForm, email: e.target.value })} required /></div>
                  <div className="form-group">
                    <label>Course</label>
                    <select value={addCertForm.course} onChange={(e) => setAddCertForm({ ...addCertForm, course: e.target.value })}>
                      <option value="Web Development-1 online">Web Development-1 online</option>
                      <option value="Web Development-1 offline">Web Development-1 offline</option>
                      <option value="Web Development-2 online">Web Development-2 online</option>
                      <option value="Linux noob to pro">Linux noob to pro</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Qualify Date</label><input type="date" value={addCertForm.qualifyDate} onChange={(e) => setAddCertForm({ ...addCertForm, qualifyDate: e.target.value })} required /></div>
                  <div className="form-group">
                    <label>Grade</label>
                    <select value={addCertForm.grade} onChange={(e) => setAddCertForm({ ...addCertForm, grade: e.target.value })}>
                      <option value="A+">A+</option><option value="A">A</option><option value="B">B</option><option value="C">C</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Exam Score</label><input type="text" value={addCertForm.examScore} onChange={(e) => setAddCertForm({ ...addCertForm, examScore: e.target.value })} /></div>
                  <div className="form-group"><label>Assignment Score</label><input type="text" value={addCertForm.assignmentScore} onChange={(e) => setAddCertForm({ ...addCertForm, assignmentScore: e.target.value })} /></div>
                  <div className="form-group"><label>Certificate ID</label><input type="text" value={addCertForm.certificateId} onChange={(e) => setAddCertForm({ ...addCertForm, certificateId: e.target.value })} required /></div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-btn" onClick={() => setIsAddCertModalOpen(false)}>Cancel</button>
                    <button type="submit" className="primary-btn" disabled={isCertAdding}>{isCertAdding ? 'Saving...' : 'Save Certificate'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* EDIT STUDENT MODAL */}
          {isEditModalOpen && editingStudent && (
            <div className="modal-backdrop" onClick={() => setIsEditModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Edit Student Details</h3>
                  <button className="close-modal-btn" onClick={() => setIsEditModalOpen(false)}>✕</button>
                </div>
                <form onSubmit={handleUpdateStudent}>
                  <div className="form-group"><label>Email Address</label><input type="email" value={editingStudent.email} onChange={(e) => setEditingStudent({ ...editingStudent, email: e.target.value })} required /></div>
                  <div className="form-group">
                    <label>Class</label>
                    <select value={editingStudent.studentClass} onChange={(e) => setEditingStudent({ ...editingStudent, studentClass: e.target.value })}>
                      <option value="Web Development-1 online">Web Development-1 online</option>
                      <option value="Web Development-1 offline">Web Development-1 offline</option>
                      <option value="Web Development-2 online">Web Development-2 online</option>
                      <option value="Linux noob to pro">Linux noob to pro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select value={editingStudent.gender || 'Male'} onChange={(e) => setEditingStudent({ ...editingStudent, gender: e.target.value })}>
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" value={editingStudent.address || ''} onChange={(e) => setEditingStudent({ ...editingStudent, address: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={editingStudent.status} onChange={(e) => setEditingStudent({ ...editingStudent, status: e.target.value })}>
                      <option value="enrolled">Enrolled</option><option value="suspend">Suspend</option><option value="removed">Removed</option>
                    </select>
                  </div>
                  <div className="modal-actions-between">
                    <button type="button" className="delete-btn" onClick={() => handleDeleteStudent(editingStudent.id)}>Delete Student</button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button type="button" className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                      <button type="submit" className="primary-btn" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* EDIT CERTIFICATE MODAL */}
          {isEditCertModalOpen && editingCert && (
            <div className="modal-backdrop" onClick={() => setIsEditCertModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header">
                  <h3>Edit Certificate Details</h3>
                  <button className="close-modal-btn" onClick={() => setIsEditCertModalOpen(false)}>✕</button>
                </div>
                <form onSubmit={handleUpdateCertificate}>
                  <div className="form-group"><label>Display Name</label><input type="text" value={editingCert.displayName || ''} onChange={(e) => setEditingCert({ ...editingCert, displayName: e.target.value })} required /></div>
                  <div className="form-group"><label>Email Address</label><input type="email" value={editingCert.email || ''} onChange={(e) => setEditingCert({ ...editingCert, email: e.target.value })} required /></div>
                  <div className="form-group">
                    <label>Course</label>
                    <select value={editingCert.course || ''} onChange={(e) => setEditingCert({ ...editingCert, course: e.target.value })}>
                      <option value="Web Development-1 online">Web Development-1 online</option>
                      <option value="Web Development-1 offline">Web Development-1 offline</option>
                      <option value="Web Development-2 online">Web Development-2 online</option>
                      <option value="Linux noob to pro">Linux noob to pro</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Qualify Date</label><input type="date" value={editingCert.qualifyDate || ''} onChange={(e) => setEditingCert({ ...editingCert, qualifyDate: e.target.value })} required /></div>
                  <div className="form-group">
                    <label>Grade</label>
                    <select value={editingCert.grade || ''} onChange={(e) => setEditingCert({ ...editingCert, grade: e.target.value })}>
                      <option value="A+">A+</option><option value="A">A</option><option value="B">B</option><option value="C">C</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Exam Score</label><input type="text" value={editingCert.examScore || ''} onChange={(e) => setEditingCert({ ...editingCert, examScore: e.target.value })} /></div>
                  <div className="form-group"><label>Assignment Score</label><input type="text" value={editingCert.assignmentScore || ''} onChange={(e) => setEditingCert({ ...editingCert, assignmentScore: e.target.value })} /></div>
                  <div className="form-group"><label>Certificate ID</label><input type="text" value={editingCert.certificateId || ''} onChange={(e) => setEditingCert({ ...editingCert, certificateId: e.target.value })} required /></div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-btn" onClick={() => setIsEditCertModalOpen(false)}>Cancel</button>
                    <button type="submit" className="primary-btn" disabled={isCertSaving}>{isCertSaving ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab !== 'Dashboard' && activeTab !== 'Users' && activeTab !== 'Student' && activeTab !== 'Form' && activeTab !== 'Certificate' && (
            <div className="tab-content-container">
              <div className="card">
                <h3>{activeTab} Content</h3>
                <p className="section-desc">Module interface for {activeTab}.</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}